import type { ComponentProps, ReactNode } from "react"
import { Flex } from "antd"
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import classNames from "classnames"
import { reorderSortableItems, type SortableResourceId } from "./reorder"
import styles from "./index.css"

export type SortableResourceRenderState = {
  isDragging: boolean
  isSortingEnabled: boolean
  sortableIndex: number | null
}

type SortableResourceListProps<Item> = {
  className?: string
  gap?: ComponentProps<typeof Flex>["gap"]
  wrap?: ComponentProps<typeof Flex>["wrap"]
  items: Item[]
  getItemAriaLabel?: (
    item: Item,
    index: number,
    sortableIndex: number,
  ) => string
  getItemClassName?: (item: Item, index: number) => string | undefined
  getItemId: (item: Item) => SortableResourceId
  isItemSortable?: (item: Item) => boolean
  renderItem: (
    item: Item,
    index: number,
    state: SortableResourceRenderState,
  ) => ReactNode
  sortable: boolean
  onReorder: (items: Item[]) => void
}

type SortableResourceItemProps<Item> = Pick<
  SortableResourceListProps<Item>,
  "getItemAriaLabel" | "getItemClassName" | "renderItem"
> & {
  index: number
  item: Item
  itemId: SortableResourceId
  sortingEnabled: boolean
  sortableIndex: number
}

const SortableResourceItem = <Item,>({
  getItemAriaLabel,
  getItemClassName,
  index,
  item,
  itemId,
  renderItem,
  sortingEnabled,
  sortableIndex,
}: SortableResourceItemProps<Item>) => {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    disabled: !sortingEnabled,
    id: itemId,
  })

  return (
    <div
      ref={setNodeRef}
      {...(sortingEnabled ? attributes : {})}
      {...(sortingEnabled ? listeners : {})}
      aria-label={
        sortingEnabled
          ? getItemAriaLabel?.(item, index, sortableIndex)
          : undefined
      }
      className={classNames(
        styles.item,
        getItemClassName?.(item, index),
        sortingEnabled && styles.itemSortable,
        isDragging && styles.itemDragging,
      )}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      {renderItem(item, index, {
        isDragging,
        isSortingEnabled: sortingEnabled,
        sortableIndex,
      })}
    </div>
  )
}

const isSortableByDefault = () => true

export const SortableResourceList = <Item,>({
  className,
  gap,
  wrap = true,
  items,
  getItemAriaLabel,
  getItemClassName,
  getItemId,
  isItemSortable = isSortableByDefault,
  renderItem,
  sortable,
  onReorder,
}: SortableResourceListProps<Item>) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const sortableItems = items.filter(isItemSortable)
  const sortableIds = sortableItems.map(getItemId)
  const sortableIndexById = new Map(
    sortableIds.map((id, index) => [id, index] as const),
  )
  const sortingEnabled = sortable && sortableItems.length > 1

  if (!items.length) return null

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return

    const nextItems = reorderSortableItems(
      items,
      active.id,
      over.id,
      getItemId,
      isItemSortable,
    )

    if (nextItems !== items) {
      onReorder(nextItems)
    }
  }

  return (
    <DndContext
      autoScroll={false}
      collisionDetection={closestCenter}
      sensors={sensors}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortableIds}
        strategy={rectSortingStrategy}
      >
        <Flex
          className={className}
          gap={gap}
          onDragStartCapture={(event) => {
            // Native image drags must not become uploads when sorting is disabled.
            // dnd-kit's pointer/keyboard sorting does not use native drag events.
            event.preventDefault()
          }}
          wrap={wrap}
        >
          {items.map((item, index) => {
            const itemId = getItemId(item)
            const sortableIndex = sortableIndexById.get(itemId)

            if (sortableIndex === undefined) {
              return (
                <div
                  className={classNames(
                    styles.item,
                    getItemClassName?.(item, index),
                  )}
                  key={itemId}
                >
                  {renderItem(item, index, {
                    isDragging: false,
                    isSortingEnabled: false,
                    sortableIndex: null,
                  })}
                </div>
              )
            }

            return (
              <SortableResourceItem
                getItemAriaLabel={getItemAriaLabel}
                getItemClassName={getItemClassName}
                index={index}
                item={item}
                itemId={itemId}
                key={itemId}
                renderItem={renderItem}
                sortingEnabled={sortingEnabled}
                sortableIndex={sortableIndex}
              />
            )
          })}
        </Flex>
      </SortableContext>
    </DndContext>
  )
}

export { reorderSortableItems } from "./reorder"
